import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  Ingredient,
  IngredientCreateInput,
  IngredientImportResult,
  IngredientListParams,
  IngredientUnitInput,
  IngredientUpdateInput,
} from '@/domain/entities/ingredient.entity'
import type { IngredientRepository } from '@/domain/repositories/ingredient.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { IngredientDto } from './ingredient.dto'
import { ingredientMapper } from './ingredient.mapper'

export class IngredientHttpRepository implements IngredientRepository {
  async list(outletId: string, params: IngredientListParams): Promise<Paged<Ingredient>> {
    const raw = await httpClient.get<unknown>('/ingredients', {
      params: {
        outlet_id: outletId,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        active: params.active !== null ? params.active : undefined,
        low_stock: params.lowStock ? 'true' : undefined,
        sellable: params.sellable !== undefined ? String(params.sellable) : undefined,
      },
    })
    const paged = unwrapPaged<IngredientDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(ingredientMapper.toIngredient) }
  }

  async getById(id: string): Promise<Ingredient> {
    const raw = await httpClient.get<unknown>(`/ingredients/${id}`)
    return ingredientMapper.toIngredient(unwrap<IngredientDto>(raw))
  }

  async create(outletId: string, input: IngredientCreateInput): Promise<Ingredient> {
    const body = {
      outlet_id: outletId,
      ...ingredientMapper.toCreateBody(input),
    }
    const raw = await httpClient.post<unknown>('/ingredients', body)
    return ingredientMapper.toIngredient(unwrap<IngredientDto>(raw))
  }

  async update(id: string, input: IngredientUpdateInput): Promise<Ingredient> {
    const body = ingredientMapper.toUpdateBody(input)
    const raw = await httpClient.put<unknown>(`/ingredients/${id}`, body)
    return ingredientMapper.toIngredient(unwrap<IngredientDto>(raw))
  }

  async setUnits(id: string, units: IngredientUnitInput[]): Promise<Ingredient> {
    const raw = await httpClient.put<unknown>(`/ingredients/${id}/units`, {
      units: units.map((u) => ({
        unit_id: u.unitId,
        per_lower: u.perLower,
      })),
    })
    return ingredientMapper.toIngredient(unwrap<IngredientDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/ingredients/${id}`)
  }

  async restore(id: string): Promise<Ingredient> {
    const raw = await httpClient.put<unknown>(`/ingredients/${id}`, { active: true })
    return ingredientMapper.toIngredient(unwrap<IngredientDto>(raw))
  }

  async importXlsx(outletId: string, file: File): Promise<IngredientImportResult> {
    const form = new FormData()
    form.append('outlet_id', outletId)
    form.append('file', file)
    const raw = await httpClient.post<unknown>('/ingredients/import', form)
    const data = unwrap<{
      ingredients?: { created?: number; updated?: number }
      failed?: Array<{ sheet: string; row: number; key: string; error: string }>
    }>(raw)
    return {
      ingredients: {
        created: data.ingredients?.created ?? 0,
        updated: data.ingredients?.updated ?? 0,
      },
      failed: (data.failed ?? []).map((f) => ({
        sheet: f.sheet,
        row: f.row,
        key: f.key,
        error: f.error,
      })),
    }
  }

  async exportXlsx(outletId: string): Promise<{ blob: Blob; filename?: string }> {
    return httpClient.download('/ingredients/export', { params: { outlet_id: outletId } })
  }
}
